import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';
import { Box } from '@wds/box';
import { defaultFonts, safeRest } from '@wds/utils';
import { IconLock, IconCheck, IconError, IconWarningFilled, IconInfoFilled } from '@wds/icons';
import { Slide } from '@wds/motion';
import { CloseButton } from '@wds/internal-components';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles$3 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    type = theme.type,
    spacing = theme.spacing;
  var toastStyles = {
    wrenchToastTitle: function () {
      return {
        color: palette.text.light,
        fontSize: type.fontSize.body,
        fontWeight: type.fontWeight.medium,
        lineHeight: "".concat(spacing[5], "px")
      };
    }
  };
  return toastStyles;
});

var ToastTitle = function ToastTitle(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var wrenchToastTitle = useStyles$3().wrenchToastTitle;
  return jsx("div", __assign({
    className: wrenchToastTitle
  }, rest, {
    children: children
  }));
};

var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    type = theme.type,
    spacing = theme.spacing;
  var toastStyles = {
    wrenchToastBody: function () {
      return {
        color: palette.text.light,
        fontSize: type.fontSize.bodySm,
        lineHeight: "".concat(spacing[5], "px"),
        '& > a': {
          color: palette.link.contrast,
          textDecoration: 'underline'
        }
      };
    }
  };
  return toastStyles;
});

var ToastBody = function ToastBody(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var wrenchToastBody = useStyles$2().wrenchToastBody;
  return jsx("div", __assign({
    className: wrenchToastBody
  }, rest, {
    children: children
  }));
};

var useStyles$1 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  var toastStyles = {
    wrenchToastFooter: function () {
      return {
        '& > *': {
          marginRight: spacing[3]
        }
      };
    }
  };
  return toastStyles;
});

var ToastFooter = function ToastFooter(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var wrenchToastFooter = useStyles$1().wrenchToastFooter;
  return jsx("div", __assign({
    className: wrenchToastFooter
  }, rest, {
    children: children
  }));
};

var useStyles = createMemoStyles(function (theme) {
  var _a;
  var palette = theme.palette,
    radius = theme.radius,
    shadow = theme.shadow,
    type = theme.type,
    spacing = theme.spacing,
    iconSize = theme.iconSize,
    breakpoints = theme.breakpoints;
  var toast = {
    padding: spacing[5],
    bgColor: palette.background.dark,
    typeColor: palette.text.light,
    borderRadius: radius.small,
    width: 350,
    titleFontSize: type.fontSize.body,
    bodyTypeSize: type.fontSize.bodySm,
    linkColor: palette.link.contrast,
    boxShadow: shadow.space
  };
  var toastStyles = {
    wrenchToastContainer: (_a = {
      boxSizing: 'border-box',
      position: 'fixed',
      right: spacing[3],
      bottom: spacing[3],
      left: spacing[3],
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space;
      }
    }, _a["@media (min-width:".concat(breakpoints.sm, "px)")] = {
      right: spacing[5],
      bottom: spacing[5],
      width: toast.width,
      left: 'auto'
    }, _a),
    wrenchToast: function () {
      return __assign(__assign({}, defaultFonts(theme)), {
        margin: 0,
        padding: toast.padding,
        backgroundColor: toast.bgColor,
        color: toast.typeColor,
        borderRadius: toast.borderRadius,
        boxShadow: toast.boxShadow
      });
    },
    wrenchToastIcon: function () {
      return {
        fontSize: iconSize.md,
        marginRight: spacing[3],
        marginTop: spacing[1],
        float: 'left',
        display: 'inline-flex'
      };
    },
    wrenchToastContent: function () {
      return {
        display: 'inline-flex'
      };
    }
  };
  return toastStyles;
});

var TIMEOUT_DELAY = 3000;
var Toast = function Toast(_a) {
  var children = _a.children,
    color = _a.color,
    onClose = _a.onClose,
    _b = _a.autoClose,
    autoClose = _b === void 0 ? true : _b,
    _c = _a.dismissible,
    dismissible = _c === void 0 ? false : _c,
    _d = _a.show,
    show = _d === void 0 ? true : _d,
    elevation = _a.elevation,
    _e = _a.ariaLabel,
    ariaLabel = _e === void 0 ? 'Close Toast' : _e,
    _f = _a.alwaysRenderContent,
    alwaysRenderContent = _f === void 0 ? false : _f,
    rest = __rest(_a, ["children", "color", "onClose", "autoClose", "dismissible", "show", "elevation", "ariaLabel", "alwaysRenderContent"]);
  var timeoutRef = useRef({
    timeout: undefined
  });
  var _g = useState(show),
    localShow = _g[0],
    setLocalShow = _g[1];
  var _h = useStyles({
      color: color,
      elevation: elevation
    }),
    wrenchToastContainer = _h.wrenchToastContainer,
    wrenchToast = _h.wrenchToast,
    wrenchToastIcon = _h.wrenchToastIcon,
    wrenchToastContent = _h.wrenchToastContent;
  var ToastIcon = color ? {
    info: IconInfoFilled,
    warning: IconWarningFilled,
    error: IconError,
    success: IconCheck,
    upgrade: IconLock
  }[color] : IconInfoFilled;
  var handleClose = function handleClose() {
    if (timeoutRef.current.timeout) {
      clearTimeout(timeoutRef.current.timeout);
    }
    setLocalShow(false);
    if (onClose) {
      onClose();
    }
  };
  var handleMouseEnter = function handleMouseEnter() {
    if (autoClose && timeoutRef.current.timeout) {
      clearTimeout(timeoutRef.current.timeout);
    }
  };
  var handleMouseExit = function handleMouseExit() {
    if (autoClose) {
      timeoutRef.current.timeout = setTimeout(function () {
        return handleClose();
      }, TIMEOUT_DELAY);
    }
  };
  useEffect(function () {
    if (autoClose && show) {
      timeoutRef.current.timeout = setTimeout(function () {
        return handleClose();
      }, TIMEOUT_DELAY);
    } else if (timeoutRef.current.timeout) {
      clearTimeout(timeoutRef.current.timeout);
    }
    setLocalShow(show);
    var timeEffectRef = timeoutRef.current.timeout;
    return function () {
      if (timeEffectRef) {
        clearTimeout(timeEffectRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  return jsx("div", {
    className: wrenchToastContainer,
    children: jsx(Slide, {
      in: localShow,
      distance: "far",
      alwaysRenderContent: alwaysRenderContent,
      children: jsxs("div", __assign({
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseExit,
        className: wrenchToast,
        role: "alert"
      }, safeRest(rest), {
        children: [dismissible && jsx(CloseButton, {
          onClick: handleClose,
          label: ariaLabel,
          variant: "light"
        }), jsxs("div", {
          className: wrenchToastContent,
          children: [color && jsx("div", {
            className: wrenchToastIcon,
            children: jsx(ToastIcon, {
              color: color,
              onDark: true,
              title: "".concat(color, " icon.")
            })
          }), jsx(Box, {
            display: "flex",
            flexDirection: "column",
            gap: 4,
            children: children
          })]
        })]
      }))
    })
  });
};
Toast.Title = ToastTitle;
Toast.Body = ToastBody;
Toast.Footer = ToastFooter;

var TOAST_COLORS = ['success', 'warning', 'error', 'info', 'upgrade'];

export { TOAST_COLORS, Toast };
